'use client';

import { useState, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';
import {
  Loader2,
  ArrowLeft,
  MapPin,
  Users,
  Target,
  HandHeart,
  Edit,
  Mail,
  Phone,
  MessageCircle,
  FileText,
  Image as ImageIcon,
  Film,
  Archive,
  File,
  ExternalLink,
  FileSpreadsheet,
  Signpost,
  Building2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { HtmlRenderer } from '@/components/ui/html-renderer';
import { ShareProjectModal } from '@/components/projects/ShareProjectModal';
import { useProject, useProjectProposals } from '@/hooks/use-projects';
import { useUser } from '@/hooks/use-user';
import { toast } from 'sonner';
import axiosInstance from '@/lib/axios';
import { cn } from '@/lib/utils';
import { getRotaryDefinitionById } from '@/lib/rotary-focus-areas';
import { getProjectAvenueName } from '@/lib/project-avenue';

const statusColors: Record<string, string> = {
  DRAFT: 'bg-gray-500',
  PENDING_APPROVAL: 'bg-yellow-500',
  PUBLISHED: 'bg-green-500',
  ARCHIVED: 'bg-red-500',
};

function formatIndianRupee(amount: number): string {
  return `₹${amount.toLocaleString('en-IN')}`;
}

function formatNumber(value: number): string {
  return value.toLocaleString('en-IN');
}

function formatDocumentSizeBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(bytes < 10240 ? 1 : 0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function friendlyDocumentKind(mime: string | null | undefined): string {
  if (!mime?.trim()) return 'File';
  const m = mime.toLowerCase();
  if (m.includes('pdf')) return 'PDF';
  if (m.startsWith('image/')) return 'Image';
  if (m.startsWith('video/')) return 'Video';
  if (m.startsWith('audio/')) return 'Audio';
  if (m.includes('spreadsheet') || m.includes('excel') || m.includes('sheet') || m === 'text/csv') {
    return 'Spreadsheet';
  }
  if (m.includes('zip') || m.includes('rar') || m.includes('compressed') || m.includes('tar')) {
    return 'Archive';
  }
  if (m.includes('word') || m.includes('msword') || m.includes('opendocument.text')) {
    return 'Document';
  }
  const tail = mime.split('/').pop() ?? 'file';
  return tail.length <= 12 ? tail.toUpperCase() : 'File';
}

function documentKindIcon(mime: string | null | undefined): LucideIcon {
  if (!mime?.trim()) return File;
  const m = mime.toLowerCase();
  if (m.includes('pdf') || m.includes('word') || m.includes('text') || m.includes('document'))
    return FileText;
  if (m.startsWith('image/')) return ImageIcon;
  if (m.startsWith('video/')) return Film;
  if (m.includes('spreadsheet') || m.includes('excel') || m.includes('sheet') || m === 'text/csv')
    return FileSpreadsheet;
  if (m.includes('zip') || m.includes('rar') || m.includes('tar')) return Archive;
  return File;
}

/** Digits-only for https://wa.me/ links */
function whatsappHref(phone: string): string {
  const digits = phone.replace(/\D/g, '');
  return digits ? `https://wa.me/${digits}` : '';
}

export default function ProjectDetailsPage() {
  const { communityId, projectId } = useParams<{ communityId: string; projectId: string }>();
  const navigate = useNavigate();
  const { project, isLoading, mutate } = useProject(communityId || '', projectId || '');
  const { proposals, isLoading: isProposalsLoading } = useProjectProposals(
    communityId || '',
    projectId || ''
  );
  const { communities: userCommunities } = useUser();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);

  const userRoles = useMemo(() => {
    if (!communityId || !userCommunities.length) return [];
    const community = userCommunities.find((c) => c.id === communityId);
    return community?.roles || [];
  }, [communityId, userCommunities]);

  const isAdmin = userRoles.includes('ADMIN');
  const isLeadership = userRoles.includes('LEADERSHIP');
  const canApprove = isAdmin || isLeadership;

  const handleSubmitForApproval = async () => {
    if (!communityId || !projectId) return;

    setIsSubmitting(true);
    try {
      await axiosInstance.post(`/api/communities/${communityId}/projects/${projectId}/submit`);
      toast.success('Project submitted for approval');
      mutate();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } } };
      toast.error(err.response?.data?.error || 'Failed to submit project');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleApprove = async () => {
    if (!communityId || !projectId) return;

    setIsSubmitting(true);
    try {
      await axiosInstance.post(`/api/communities/${communityId}/projects/${projectId}/approve`);
      toast.success('Project approved and published');
      mutate();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } } };
      toast.error(err.response?.data?.error || 'Failed to approve project');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenForVendors = async () => {
    if (!communityId || !projectId) return;

    setIsSubmitting(true);
    try {
      await axiosInstance.post(`/api/communities/${communityId}/projects/${projectId}/publish`, {
        openForVendors: true,
      });
      toast.success('Project is now open for vendors');
      mutate();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } } };
      toast.error(err.response?.data?.error || 'Failed to open project for vendors');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseForVendors = async () => {
    if (!communityId || !projectId) return;

    setIsSubmitting(true);
    try {
      await axiosInstance.post(`/api/communities/${communityId}/projects/${projectId}/publish`, {
        openForVendors: false,
      });
      toast.success('Project is no longer open for vendors');
      mutate();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } } };
      toast.error(err.response?.data?.error || 'Failed to close project for vendors');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUnpublish = async () => {
    if (!communityId || !projectId) return;

    setIsSubmitting(true);
    try {
      await axiosInstance.post(`/api/communities/${communityId}/projects/${projectId}/unpublish`);
      toast.success('Project has been unpublished');
      mutate();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } } };
      toast.error(err.response?.data?.error || 'Failed to unpublish project');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-[50vh] w-full items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="container py-8">
        <p className="text-muted-foreground">Project not found</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-12 px-3 md:px-0">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
        <div className="flex items-start gap-2 md:gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to={`/${communityId}/projects`}>
              <ArrowLeft className="h-5 w-5" />
            </Link>
          </Button>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-xl md:text-3xl font-bold tracking-tight truncate">
                {project.name}
              </h1>
              <Badge
                className={`${statusColors[project.status] || 'bg-gray-500'} text-white shrink-0`}
              >
                {project.status.replace('_', ' ')}
              </Badge>
              {project.isDistrictProject && (
                <Badge variant="outline" className="border-primary/30 text-primary shrink-0">
                  District Project
                </Badge>
              )}
              {getProjectAvenueName(project) && (
                <Badge variant="secondary" className="gap-1 shrink-0">
                  <Signpost className="h-3 w-3" />
                  {getProjectAvenueName(project)}
                </Badge>
              )}
            </div>
            {project.creator && (
              <p className="text-muted-foreground mt-1 text-sm">
                Created by {project.creator.firstName} {project.creator.lastName}
              </p>
            )}
          </div>
        </div>
        <Button
          variant="outline"
          onClick={() => navigate(`/${communityId}/projects/${projectId}/edit`)}
          className="w-full md:w-auto shrink-0"
        >
          <Edit className="mr-2 h-4 w-4" />
          Edit Project
        </Button>
      </div>

      {project.bannerImageUrl && project.bannerImageUrl.trim().length > 0 ? (
        <div className="w-full rounded-lg overflow-hidden border border-border bg-muted/40">
          <img src={project.bannerImageUrl} alt="" className="w-full max-h-[40vh] object-contain" />
        </div>
      ) : null}

      <div className="grid gap-6 lg:grid-cols-3 overflow-x-auto min-w-0">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6 min-w-0">
          {/* Rotary focus */}
          {project.rotaryFocusAreaKeys && project.rotaryFocusAreaKeys.length > 0 ? (
            <Card>
              <CardHeader>
                <CardTitle>Rotary areas of focus</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {project.rotaryFocusAreaKeys!.map((id) => {
                  const def = getRotaryDefinitionById(id);
                  return (
                    <div key={id} className="border-b pb-4 last:border-0 last:pb-0 space-y-1">
                      <p className="font-medium">{def?.title ?? id}</p>
                      {def ? (
                        <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                          {def.description}
                        </p>
                      ) : (
                        <p className="text-xs text-muted-foreground">
                          Definition not found in src/data/rotary-focus-areas.json.
                        </p>
                      )}
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          ) : null}

          {/* Downloadable documents */}
          {project.documents && project.documents.length > 0 ? (
            <Card>
              <CardHeader className="pb-4">
                <CardTitle className="text-lg">Documents</CardTitle>
                <CardDescription>
                  {project.documents.length} downloadable file
                  {project.documents.length === 1 ? '' : 's'} for this project
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {project.documents.map((doc) => {
                  const Icon = documentKindIcon(doc.mimeType);
                  const kind = friendlyDocumentKind(doc.mimeType);
                  const sizeLabel =
                    doc.size != null && doc.size > 0 ? formatDocumentSizeBytes(doc.size) : null;
                  const meta = [kind, sizeLabel].filter(Boolean).join(' · ');

                  return (
                    <a
                      key={doc.id}
                      href={doc.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={cn(
                        'group flex items-center gap-4 rounded-xl border border-border/70 bg-gradient-to-b from-card to-muted/20 p-4 shadow-sm',
                        'transition-colors hover:border-primary/35 hover:from-muted/30 hover:to-muted/50',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
                      )}
                    >
                      <div
                        className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary shadow-inner"
                        aria-hidden
                      >
                        <Icon className="h-6 w-6" strokeWidth={1.75} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-medium leading-snug text-foreground transition-colors group-hover:text-primary break-words">
                          {doc.name?.trim() || 'Untitled'}
                        </p>
                        {meta ? <p className="mt-1 text-xs text-muted-foreground">{meta}</p> : null}
                      </div>
                      <div className="flex shrink-0 items-center gap-1.5 text-sm font-medium text-primary">
                        <span>Open</span>
                        <ExternalLink
                          className="h-4 w-4 opacity-80 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                          aria-hidden
                        />
                      </div>
                    </a>
                  );
                })}
              </CardContent>
            </Card>
          ) : null}

          {/* Description */}
          {project.description && (
            <Card>
              <CardHeader>
                <CardTitle>Description</CardTitle>
              </CardHeader>
              <CardContent>
                <HtmlRenderer html={project.description} />
              </CardContent>
            </Card>
          )}

          {/* Need Assessment */}
          {project.needAssessment && (
            <Card>
              <CardHeader>
                <CardTitle>Need Assessment</CardTitle>
              </CardHeader>
              <CardContent>
                <HtmlRenderer html={project.needAssessment} />
              </CardContent>
            </Card>
          )}

          {/* Items */}
          <Card>
            <CardHeader>
              <CardTitle>Items Required</CardTitle>
            </CardHeader>
            <CardContent>
              {project.items && project.items.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-muted-foreground">
                        <th className="pb-3 pr-4 text-left font-medium">Item</th>
                        <th className="pb-3 px-4 text-right font-medium w-24">Qty</th>
                        <th className="pb-3 pl-4 text-right font-medium w-28">Unit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {project.items.map((item) => (
                        <tr key={item.id} className="border-b last:border-0 align-top">
                          <td className="py-3 pr-4">
                            <p className="font-medium">{item.name}</p>
                            {item.description && (
                              <p className="text-muted-foreground mt-1">{item.description}</p>
                            )}
                          </td>
                          <td className="py-3 px-4 text-right font-medium whitespace-nowrap">
                            {formatNumber(item.quantity)}
                          </td>
                          <td className="py-3 pl-4 text-right text-muted-foreground whitespace-nowrap">
                            {item.unit || '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-muted-foreground">No items added yet.</p>
              )}
            </CardContent>
          </Card>

          {/* Contact details */}
          {project.contacts && project.contacts.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Contact details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {project.contacts.map((c) => (
                  <div key={c.id} className="border-b pb-4 last:border-0 last:pb-0 space-y-2">
                    {c.name?.trim() ? <p className="font-medium">{c.name.trim()}</p> : null}
                    {c.designation?.trim() ? (
                      <p className="text-sm text-muted-foreground">{c.designation.trim()}</p>
                    ) : null}
                    <div className="flex flex-col gap-2 text-sm">
                      {c.phone?.trim() && (
                        <a
                          href={`tel:${c.phone.trim()}`}
                          className="flex flex-wrap items-center gap-x-2 gap-y-1 text-primary hover:underline"
                        >
                          <Phone className="h-4 w-4 shrink-0 text-muted-foreground" />
                          <span>{c.phone.trim()}</span>
                          {c.phoneOnWhatsApp && (
                            <Badge variant="outline" className="text-xs font-normal">
                              On WhatsApp
                            </Badge>
                          )}
                        </a>
                      )}
                      {c.phoneOnWhatsApp && c.phone?.trim() && (
                        <a
                          href={whatsappHref(c.phone)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 text-primary hover:underline w-fit"
                        >
                          <MessageCircle className="h-4 w-4" />
                          Open in WhatsApp
                        </a>
                      )}
                      {c.email?.trim() && (
                        <a
                          href={`mailto:${c.email.trim()}`}
                          className="flex items-center gap-2 text-primary hover:underline break-all"
                        >
                          <Mail className="h-4 w-4 shrink-0 text-muted-foreground" />
                          {c.email.trim()}
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Shared Communities */}
        {project.shares && project.shares.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Shared With</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {project.shares.map((share) => (
                  <div key={share.id} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {share.community?.logo ? (
                        <img src={share.community.logo} alt="" className="h-6 w-6 rounded-full" />
                      ) : (
                        <div className="h-6 w-6 rounded-full bg-muted" />
                      )}
                      <span className="font-medium">{share.community?.name}</span>
                    </div>
                    <Badge variant="outline">{share.status}</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Vendor Proposals */}
        {project.isOpenForVendors && (
          <Card>
            <CardHeader>
              <CardTitle>Vendor Proposals</CardTitle>
            </CardHeader>
            <CardContent>
              {isProposalsLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : proposals.length > 0 ? (
                <div className="space-y-4">
                  {proposals.map((proposal) => {
                    const vendorName = [proposal.vendor?.firstName, proposal.vendor?.lastName]
                      .filter(Boolean)
                      .join(' ');
                    const businessHref =
                      proposal.vendorId && proposal.businessId
                        ? `/${communityId}/ub/${proposal.vendorId}/${proposal.businessId}`
                        : proposal.vendorId
                          ? `/${communityId}/u/${proposal.vendorId}`
                          : null;
                    const vendorHref = proposal.vendorId
                      ? `/${communityId}/u/${proposal.vendorId}`
                      : null;

                    return (
                      <div
                        key={proposal.id}
                        className="rounded-xl border border-foreground/15 bg-background p-4 space-y-3"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex min-w-0 items-center gap-2.5">
                            {proposal.business?.logo ? (
                              <img
                                src={proposal.business.logo}
                                alt=""
                                className="h-9 w-9 rounded-full object-cover shrink-0"
                              />
                            ) : (
                              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">
                                {(proposal.business?.name || vendorName || '?')
                                  .charAt(0)
                                  .toUpperCase()}
                              </div>
                            )}
                            <div className="min-w-0">
                              {proposal.business?.name ? (
                                businessHref ? (
                                  <Link
                                    to={businessHref}
                                    className="block truncate text-sm font-semibold text-foreground hover:text-primary hover:underline"
                                  >
                                    {proposal.business.name}
                                  </Link>
                                ) : (
                                  <p className="truncate text-sm font-semibold text-foreground">
                                    {proposal.business.name}
                                  </p>
                                )
                              ) : null}
                              {vendorName ? (
                                <p className="text-xs text-foreground">
                                  Submitted by:{' '}
                                  {vendorHref ? (
                                    <Link
                                      to={vendorHref}
                                      className="font-medium text-primary hover:underline"
                                    >
                                      {vendorName}
                                    </Link>
                                  ) : (
                                    <span className="font-medium">{vendorName}</span>
                                  )}
                                </p>
                              ) : null}
                            </div>
                          </div>
                          <Badge variant="outline" className="shrink-0 text-xs">
                            {proposal.status}
                          </Badge>
                        </div>

                        {proposal.items && proposal.items.length > 0 ? (
                          <div className="space-y-2">
                            <p className="text-xs font-semibold uppercase tracking-wide text-foreground">
                              Items proposed for ({proposal.items.length})
                            </p>
                            <ul className="space-y-2">
                              {proposal.items.map((item) => (
                                <li
                                  key={item.id}
                                  className="rounded-lg border border-foreground/15 bg-muted/40 px-3 py-2.5"
                                >
                                  <div className="flex items-start justify-between gap-3">
                                    <p className="min-w-0 text-sm font-semibold text-foreground">
                                      {item.name}
                                    </p>
                                    <p className="shrink-0 text-sm font-medium text-foreground">
                                      Qty: {formatNumber(item.quantity)}
                                      {item.unit ? ` ${item.unit}` : ''}
                                    </p>
                                  </div>
                                  {item.proposalMessage?.trim() ? (
                                    <p className="mt-1.5 text-sm text-foreground whitespace-pre-wrap">
                                      {item.proposalMessage}
                                    </p>
                                  ) : (
                                    <p className="mt-1.5 text-sm italic text-foreground/70">
                                      Showed interest (no note)
                                    </p>
                                  )}
                                </li>
                              ))}
                            </ul>
                          </div>
                        ) : (
                          <p className="text-sm text-foreground/70 italic">
                            No specific items listed on this proposal.
                          </p>
                        )}

                        {proposal.message?.trim() ? (
                          <div className="rounded-lg border border-foreground/10 bg-muted/30 px-3 py-2">
                            <p className="text-xs font-semibold text-foreground mb-1">
                              Additional notes
                            </p>
                            <p className="text-sm text-foreground whitespace-pre-wrap">
                              {proposal.message}
                            </p>
                          </div>
                        ) : null}

                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-foreground">
                          {proposal.community && <span>From: {proposal.community.name}</span>}
                          <span>
                            {new Date(proposal.createdAt).toLocaleString(undefined, {
                              dateStyle: 'medium',
                              timeStyle: 'short',
                            })}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-muted-foreground text-sm">No proposals yet.</p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Details */}
          <Card>
            <CardHeader>
              <CardTitle>Project Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {project.location && (
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                  <span>{project.location}</span>
                </div>
              )}
              {project.happeningFor && (
                <div className="flex items-center gap-2">
                  <Target className="h-4 w-4 text-primary" />
                  <div>
                    <span className="text-muted-foreground">For: </span>
                    <span>{project.happeningFor}</span>
                  </div>
                </div>
              )}
              {project.hostClub && (
                <div className="flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <span className="text-muted-foreground">Host club: </span>
                    <span>{project.hostClub}</span>
                  </div>
                </div>
              )}
              {project.estimatedBeneficiaries && (
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  <span>{formatNumber(project.estimatedBeneficiaries)} beneficiaries</span>
                </div>
              )}
              {project.expectedCost && (
                <div className="flex items-center gap-2">
                  <HandHeart className="h-4 w-4 text-muted-foreground" />
                  <span>{formatIndianRupee(project.expectedCost)}</span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Actions */}
          {project.status === 'DRAFT' && (
            <Card>
              <CardHeader>
                <CardTitle>Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button
                  className="w-full"
                  variant="default"
                  onClick={handleSubmitForApproval}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    'Submit for Approval'
                  )}
                </Button>
              </CardContent>
            </Card>
          )}

          {project.status === 'PENDING_APPROVAL' && (
            <Card>
              <CardHeader>
                <CardTitle>Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {canApprove ? (
                  <Button
                    className="w-full"
                    variant="default"
                    onClick={handleApprove}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Approving...
                      </>
                    ) : (
                      'Approve & Publish'
                    )}
                  </Button>
                ) : (
                  <p className="text-sm text-muted-foreground">Waiting for leadership approval.</p>
                )}
              </CardContent>
            </Card>
          )}

          {project.status === 'PUBLISHED' && (
            <Card>
              <CardHeader>
                <CardTitle>Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button
                  className="w-full"
                  variant="outline"
                  onClick={() => setIsShareModalOpen(true)}
                >
                  Share Project
                </Button>
                {project.isOpenForVendors ? (
                  <Button
                    className="w-full"
                    variant="outline"
                    onClick={handleCloseForVendors}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Closing...
                      </>
                    ) : (
                      'Close for Vendors'
                    )}
                  </Button>
                ) : (
                  <Button
                    className="w-full"
                    variant="outline"
                    onClick={handleOpenForVendors}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Opening...
                      </>
                    ) : (
                      'Open for Vendors'
                    )}
                  </Button>
                )}
                <Button
                  className="w-full"
                  variant="destructive"
                  onClick={handleUnpublish}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Unpublishing...
                    </>
                  ) : (
                    'Unpublish Project'
                  )}
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      <ShareProjectModal
        open={isShareModalOpen}
        onOpenChange={setIsShareModalOpen}
        communityId={communityId || ''}
        projectId={projectId || ''}
        onSuccess={mutate}
      />
    </div>
  );
}
